import { useState, useEffect, useRef, useCallback } from 'react';
import { Copy, Check, SpeakerHigh, Stop, ThumbsUp, ThumbsDown, ArrowClockwise } from '@phosphor-icons/react';

interface Message {
  id: string;
  role: string;
  content: string;
}

const FEEDBACK_REASONS = [
  'Incorrect or incomplete',
  'Not what I asked',
  'Too generic',
  'Tone was off',
  'Safety concern',
  'Other',
];

function stripMarkdown(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, '') // code blocks
    .replace(/`([^`]+)`/g, '$1') // inline code
    .replace(/#{1,6}\s+/g, '') // headers
    .replace(/\*\*([^*]+)\*\*/g, '$1') // bold
    .replace(/\*([^*]+)\*/g, '$1') // italic
    .replace(/__([^_]+)__/g, '$1') // bold alt
    .replace(/_([^_]+)_/g, '$1') // italic alt
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
    .replace(/^[-*+]\s+/gm, '') // bullets
    .replace(/^\d+\.\s+/gm, '') // numbered lists
    .replace(/>\s+/g, '') // blockquotes
    .replace(/\n{2,}/g, '. ')
    .trim();
}

interface MessageActionsProps {
  message: Message;
  isLastAssistant: boolean;
  isStreaming: boolean;
  chatId: string | null;
  onRegenerate: () => void;
  onFeedback: (messageId: string, value: 'up' | 'down', reasons?: string[], text?: string) => void;
  onRemoveFeedback?: (messageId: string) => void;
}

export default function MessageActions({
  message,
  isLastAssistant,
  isStreaming,
  chatId,
  onRegenerate,
  onFeedback,
  onRemoveFeedback,
}: MessageActionsProps) {
  const [copied, setCopied] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [feedbackValue, setFeedbackValue] = useState<'up' | 'down' | null>(null);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackReasons, setFeedbackReasons] = useState<string[]>([]);
  const [feedbackText, setFeedbackText] = useState('');
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  // Load cached feedback from localStorage
  useEffect(() => {
    if (!chatId) return;
    const key = `feedback:${chatId}:${message.id}`;
    const cached = localStorage.getItem(key);
    if (cached === 'up' || cached === 'down') {
      setFeedbackValue(cached);

    }
  }, [chatId, message.id]);

  // Cancel speech on unmount. Guard for runtimes without Web Speech API
  // (some Capacitor WebViews) — otherwise the cleanup throws ReferenceError
  // on every assistant message unmount.
  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = message.content;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [message.content]);

  const ttsAvailable = typeof window !== 'undefined' && 'speechSynthesis' in window;

  const handleReadAloud = useCallback(() => {
    if (!ttsAvailable) return;
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }

    const text = stripMarkdown(message.content);
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
    setIsSpeaking(true);
  }, [isSpeaking, message.content, ttsAvailable]);

  const clearFeedback = useCallback(() => {
    setFeedbackValue(null);
    setShowFeedbackForm(false);
    setFeedbackReasons([]);
    setFeedbackText('');

    if (chatId) {
      localStorage.removeItem(`feedback:${chatId}:${message.id}`);
    }
    onRemoveFeedback?.(message.id);
  }, [chatId, message.id, onRemoveFeedback]);

  const handleThumbsUp = useCallback(() => {
    if (feedbackValue === 'up') {
      clearFeedback();
      return;
    }
    setFeedbackValue('up');
    setShowFeedbackForm(false);
    if (chatId) {
      localStorage.setItem(`feedback:${chatId}:${message.id}`, 'up');
    }
    onFeedback(message.id, 'up');
  }, [feedbackValue, chatId, message.id, onFeedback, clearFeedback]);

  const handleThumbsDown = useCallback(() => {
    if (feedbackValue === 'down') {
      clearFeedback();
      return;
    }
    setFeedbackValue('down');
    setShowFeedbackForm(true);
  }, [feedbackValue, clearFeedback]);

  const handleSubmitFeedback = useCallback(() => {
    if (chatId) {
      localStorage.setItem(`feedback:${chatId}:${message.id}`, 'down');
    }
    onFeedback(message.id, 'down', feedbackReasons, feedbackText || undefined);
    setShowFeedbackForm(false);
  }, [chatId, message.id, feedbackReasons, feedbackText, onFeedback]);

  const toggleReason = useCallback((reason: string) => {
    setFeedbackReasons((prev) =>
      prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]
    );
  }, []);

  const hasInteraction = feedbackValue !== null || showFeedbackForm;

  return (
    <div className="mt-1.5">
      {/* Action buttons row */}
      <div
        className={`flex items-center gap-0.5 ${
          hasInteraction
            ? 'opacity-100'
            : 'opacity-100 md:opacity-0 md:group-hover:opacity-100'
        } transition-opacity`}
      >
        <button
          onClick={handleCopy}
          className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          title="Copy"
        >
          {copied ? <Check size={18} weight="bold" /> : <Copy size={18} />}
        </button>

        {ttsAvailable && (
          <button
            onClick={handleReadAloud}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            title={isSpeaking ? 'Stop' : 'Read aloud'}
          >
            {isSpeaking ? <Stop size={18} weight="fill" /> : <SpeakerHigh size={18} />}
          </button>
        )}

        <button
          onClick={handleThumbsUp}
          className={`p-2 rounded-md transition-colors ${
            feedbackValue === 'up'
              ? 'text-pink-500'
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
          }`}
          title="Good response"
        >
          <ThumbsUp size={18} weight={feedbackValue === 'up' ? 'fill' : 'regular'} />
        </button>

        <button
          onClick={handleThumbsDown}
          className={`p-2 rounded-md transition-colors ${
            feedbackValue === 'down'
              ? 'text-pink-500'
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
          }`}
          title="Bad response"
        >
          <ThumbsDown size={18} weight={feedbackValue === 'down' ? 'fill' : 'regular'} />
        </button>

        {isLastAssistant && !isStreaming && (
          <button
            onClick={onRegenerate}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            title="Regenerate response"
          >
            <ArrowClockwise size={18} />
          </button>
        )}
      </div>

      {/* Thumbs down feedback form */}
      {showFeedbackForm && (
        <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">
            What went wrong?
          </p>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {FEEDBACK_REASONS.map((reason) => (
              <button
                key={reason}
                onClick={() => toggleReason(reason)}
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                  feedbackReasons.includes(reason)
                    ? 'bg-pink-100 dark:bg-pink-900/30 border-pink-300 dark:border-pink-700 text-pink-700 dark:text-pink-300'
                    : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
              >
                {reason}
              </button>
            ))}
          </div>
          <textarea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder="Share details (optional)"
            className="w-full text-xs bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-2 text-gray-700 dark:text-gray-200 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:border-pink-400 resize-none"
            rows={2}
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={handleSubmitFeedback}
              className="px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-orange-500 to-pink-500 rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all"
            >
              Submit
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
